import { useState, useEffect } from 'react'

import { loadScript } from '@paypal/paypal-js'

import cspNonce from 'src/utils/cspNonce'

// Single source of truth for the PayPal/Venmo SDK on this page.
//
// @paypal/paypal-js removes and re-injects the SDK <script> when called
// with different options, which destroys any live zoid components from
// the previous load and surfaces as "zoid destroyed all components" plus
// vanishing iframes. Keeping all callers (useVenmo, PaypalButton) wired
// to the same hook means there's only ever one set of options in flight
// — when the params do legitimately change (currency, vault id_token),
// the reload happens before any consumer has rendered live components,
// not after.
//
// Returns { sdk, error }. `error` is what lets callers tell "still loading"
// from "never going to load" — otherwise PaypalButton spins forever.
const usePaypalSdk = ({ enabled, clientId, currency, idToken, sandbox }) => {
  const [ sdk, setSdk ] = useState(null)
  const [ error, setError ] = useState(null)

  useEffect(() => {
    if (!enabled || !clientId) return

    let cancelled = false
    setSdk(null)
    setError(null)

    loadScript({
      'client-id': clientId,
      'data-csp-nonce': cspNonce(),
      'enable-funding': 'venmo',
      currency,
      ...(idToken ? { 'data-user-id-token': idToken } : {}),
      ...(sandbox ? { 'buyer-country': 'US' } : {}),
    })
      .then(loaded => { if (!cancelled) setSdk(loaded) })
      .catch(err => {
        console.error('Failed to load PayPal JS SDK script', err)
        if (!cancelled) setError(err)
      })

    return () => { cancelled = true }
  }, [ enabled, clientId, currency, idToken, sandbox ])

  return { sdk, error }
}

export default usePaypalSdk
